// What something reaches as the shared diagram's cards and lines: a head card (an endpoint, a
// test) on the left, then a card per file with the members reached in it, call lines from member
// to member, and a dashed line where a call through an interface or a virtual member goes on to an
// implementation, or a value handed to a package goes on to its probable handler. The endpoint map
// and the test map draw this way.
import type { CodeMap, Reach } from '@modelviewer/core';
import { approximateMeasure, sizeNode, type EntityNodeData, type Graph, type GraphEdge, type Line, type Measure, type PropertyMode } from '../diagram/graph';
import { codeIndex, idLabel } from './codeIndex';
import { MEMBER_ICON, rowName } from './graph';

export interface ReachHead {
  /** The head card's id: never a file path. */
  id: string;
  key: string;
  label: string;
  badges: string[];
  /** Shown under the title. */
  table: string;
  lines: Line[];
  /** Lines from the head card to the members it starts from. */
  starts: { id: string; member: string; label: string }[];
  /** Calls whose `from` is this id are drawn from the head card (a minimal API lambda's calls). */
  own?: { id: string; label: string };
}

const blank = { required: false, unique: false, requiredDependent: false, relationship: null };

/** A row on the head card that is not a member: data reached, a type faked. */
export function headRow(id: string, name: string, type: string, member: 'data' | 'fake'): Line {
  return { kind: 'row', row: { id, name, type, optional: false, pk: false, fk: false, ak: false, generated: false, concurrency: false, depth: 0, member } };
}

export function buildReachGraph(map: CodeMap, reach: Reach, head: ReachHead, options: { members: PropertyMode; measure?: Measure }): Graph {
  const index = codeIndex(map);
  const measure = options.measure ?? approximateMeasure;
  const reached = new Set([...reach.depth.keys(), ...reach.types]);
  const fileOf = (id: string) => (id === head.own?.id ? head.id : index.fileOf(id));

  const nodes: EntityNodeData[] = [sizeNode({ id: head.id, key: head.key, label: head.label, namespace: null, schema: null, badges: head.badges, base: null, table: head.table, lines: head.lines, anchors: [], width: 0, height: 0 }, measure)];

  // A card per file: its types with something reached, and the members reached in them.
  for (const path of reach.files) {
    const file = map.files.find((f) => f.path === path);
    if (!file) continue;
    const lines: Line[] = [];
    for (const typeId of file.types) {
      const t = index.types.get(typeId);
      if (!t) continue;
      const members = (index.membersOf.get(typeId) ?? []).filter((m) => m.file === path && reached.has(m.id));
      if (!members.length && !reached.has(typeId)) continue;
      lines.push({
        kind: 'section',
        section: { id: `type:${t.id}`, kind: 'type', name: t.typeParams.length ? `${t.name}<${t.typeParams.join(', ')}>` : t.name, type: t.kind, collection: false, json: false, optional: false, depth: 0 },
      });
      if (options.members === 'none') continue;
      for (const m of members) {
        lines.push({
          kind: 'row',
          row: { id: m.id, name: rowName(m), type: m.kind === 'constructor' ? '' : (m.valueType ?? ''), optional: false, pk: false, fk: false, ak: false, generated: false, concurrency: false, depth: 1, member: MEMBER_ICON[m.kind] },
        });
      }
    }
    if (!lines.length) continue;
    nodes.push(
      sizeNode(
        { id: path, key: path, label: path.slice(path.lastIndexOf('/') + 1), namespace: file.namespace, schema: path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '(root)', badges: [], base: null, table: path, lines, anchors: [], width: 0, height: 0 },
        measure,
      ),
    );
  }

  const edges: GraphEdge[] = [];
  // The head card to the members it starts from (an endpoint's handler, a test and what runs with it).
  for (const s of head.starts) {
    const target = index.fileOf(s.member);
    if (target) edges.push({ id: s.id, kind: 'call', source: head.id, target, sourceRow: null, targetRow: s.member, ...blank, label: s.label });
  }
  const calls = new Map<string, { from: string; to: string; count: number; exact: boolean }>();
  for (const c of reach.calls) {
    const key = `${c.from}->${c.to}`;
    const g = calls.get(key);
    if (g) {
      g.count++;
      g.exact ||= c.certainty === 'exact';
    } else calls.set(key, { from: c.from, to: c.to, count: 1, exact: c.certainty === 'exact' });
  }
  for (const [key, c] of calls) {
    const source = fileOf(c.from);
    const target = fileOf(c.to);
    if (!source || !target) continue;
    const own = c.from === head.own?.id;
    edges.push({
      id: `call:${key}`,
      kind: 'call',
      source,
      target,
      sourceRow: own ? null : c.from,
      targetRow: index.members.has(c.to) ? c.to : null,
      ...blank,
      label: `${own ? head.own!.label : idLabel(c.from, index)} → ${idLabel(c.to, index)}${c.count > 1 ? ` ×${c.count}` : ''}`,
      guess: !c.exact,
      count: c.count,
    });
  }
  // Handed to a package (`sender.Send(new PlaceOrder(...))`), probably handled by the code's handler: a guess.
  for (const h of reach.handled) {
    const own = h.from === head.own?.id;
    const source = fileOf(h.from);
    const target = fileOf(h.to);
    if (!source || !target) continue;
    edges.push({
      id: `handled:${h.from}->${h.to}`,
      kind: 'call',
      source,
      target,
      sourceRow: own ? null : h.from,
      targetRow: h.to,
      ...blank,
      label: `${idLabel(h.type, index)} handed to ${h.via}, probably handled by ${idLabel(h.to, index)}`,
      guess: true,
    });
  }
  // An HTTP request into an endpoint of the code: to its handler, or to the member mapping its handler lambda.
  for (const r of reach.requests) {
    const endpoint = map.endpoints.find((e) => e.id === r.endpoint);
    const source = fileOf(r.from);
    const target = r.to ? index.fileOf(r.to) : endpoint?.file;
    if (!source || !target) continue;
    edges.push({
      id: `request:${r.from}->${r.endpoint}`,
      kind: 'call',
      source,
      target,
      sourceRow: r.from,
      targetRow: r.to,
      ...blank,
      label: `${r.method} ${r.url} → ${r.endpoint}`,
      guess: r.certainty === 'guess',
    });
  }
  for (const d of reach.dispatch) {
    const source = fileOf(d.from);
    const target = fileOf(d.to);
    if (!source || !target) continue;
    edges.push({ id: `impl:${d.from}->${d.to}`, kind: 'uses', source, target, sourceRow: d.from, targetRow: d.to, ...blank, label: `${idLabel(d.from, index)} is implemented by ${idLabel(d.to, index)}` });
  }

  // Lines attach to rows on their cards, or to the card.
  const rowsOf = new Map(nodes.map((n) => [n.id, new Set(n.lines.flatMap((l) => (l.kind === 'row' ? [l.row.id] : [])))]));
  const shown = edges
    .filter((e) => rowsOf.has(e.source) && rowsOf.has(e.target))
    .map((e) => {
      const sourceRow = e.sourceRow && rowsOf.get(e.source)!.has(e.sourceRow) ? e.sourceRow : null;
      const targetRow = e.targetRow && rowsOf.get(e.target)!.has(e.targetRow) ? e.targetRow : null;
      return sourceRow === e.sourceRow && targetRow === e.targetRow ? e : { ...e, sourceRow, targetRow };
    });
  const byId = new Map(nodes.map((n) => [n.id, n]));
  for (const e of shown) {
    if (e.sourceRow) byId.get(e.source)!.anchors.push(e.sourceRow);
    if (e.targetRow) byId.get(e.target)!.anchors.push(e.targetRow);
  }
  for (const n of nodes) n.anchors = [...new Set(n.anchors)];
  return { nodes, edges: shown };
}
